<template>
  <div class="dataset-page">
    <section class="dataset-shell">
      <div class="dataset-hero">
        <div class="hero-copy">
          <nav class="dataset-breadcrumb" aria-label="Breadcrumb">
            <span>Home</span>
            <span class="breadcrumb-separator">/</span>
            <span>Data Inventory</span>
            <span class="breadcrumb-separator">/</span>
            <span class="breadcrumb-current">All Datasets</span>
          </nav>

          <p class="hero-kicker">Dataset Review Suite</p>
          <h1>Dataset inventory with a more refined control room feel</h1>
          <p class="hero-description">
            Monitor submitted datasets, separate pending reviews from approved records, and navigate
            the inventory through a cleaner, more executive workspace.
          </p>
        </div>

        <div class="hero-metrics">
          <article class="metric-card">
            <span class="metric-label">Inventory size</span>
            <strong class="metric-value">{{ datasets.length }}</strong>
            <span class="metric-meta">Datasets currently in view</span>
          </article>
          <article class="metric-card">
            <span class="metric-label">Pending</span>
            <strong class="metric-value">{{ pendingCount }}</strong>
            <span class="metric-meta">Awaiting approval</span>
          </article>
          <article class="metric-card">
            <span class="metric-label">Approved</span>
            <strong class="metric-value">{{ approvedCount }}</strong>
            <span class="metric-meta">Ready for downstream use</span>
          </article>
        </div>
      </div>

      <section class="toolbar">
        <div class="status-switcher" role="tablist" aria-label="Dataset status filters">
          <button
            type="button"
            class="status-pill"
            :class="{ 'status-pill--active': isPending }"
            @click="changePend"
          >
            Pending Datasets
            <span class="pill-count">{{ pendingCount }}</span>
          </button>
          <button
            type="button"
            class="status-pill"
            :class="{ 'status-pill--active': isApproved }"
            @click="changeApprov"
          >
            Approved Datasets
            <span class="pill-count">{{ approvedCount }}</span>
          </button>
          <button
            type="button"
            class="status-pill"
            :class="{ 'status-pill--active': isAll }"
            @click="changeAll"
          >
            All Datasets
            <span class="pill-count">{{ datasets.length }}</span>
          </button>
        </div>

        <label class="search-shell" for="dataset-search">
          <i class="fas fa-search" aria-hidden="true"></i>
          <input
            id="dataset-search"
            v-model.trim="search"
            type="search"
            placeholder="Search datasets by name"
          />
        </label>
      </section>

      <b-row class="content-grid" no-gutters>
        <b-col cols="12" xl="9" class="content-main">
          <div v-if="loading" class="loading-state">
            <b-spinner />
            <p>Curating dataset inventory...</p>
          </div>

          <template v-else>
            <div class="results-header">
              <div>
                <p class="results-kicker">Inventory view</p>
                <h2>{{ currentViewTitle }}</h2>
              </div>
              <div class="results-meta">
                <span
                  >{{ computedData.length }} result{{ computedData.length === 1 ? '' : 's' }}</span
                >
                <span class="results-divider"></span>
                <span>Page {{ currentPage }} of {{ totalPages }}</span>
              </div>
            </div>

            <div v-if="ComputedDatasets.length" class="dataset-list">
              <article
                v-for="(dataset, index) in ComputedDatasets"
                :key="dataset.id || index"
                class="premium-dataset-card"
                @click="openDataset(dataset.id)"
              >
                <div class="card-glow"></div>

                <div class="dataset-card__top">
                  <div>
                    <span class="dataset-index">Dataset {{ padIndex(index) }}</span>
                    <h3>{{ dataset.name }}</h3>
                  </div>

                  <span class="status-badge" :class="statusBadgeClass(getDatasetStatus(dataset))">
                    {{ getDatasetStatus(dataset) }}
                  </span>
                </div>

                <p class="dataset-summary">
                  {{ getDatasetSummary(dataset) }}
                </p>

                <div class="dataset-detail-grid">
                  <div class="detail-card">
                    <span class="detail-label">Coverage window</span>
                    <strong>{{ getTimeline(dataset) }}</strong>
                  </div>
                  <div class="detail-card">
                    <span class="detail-label">Program areas</span>
                    <strong>{{ getProgramAreas(dataset) }}</strong>
                  </div>
                  <div class="detail-card">
                    <span class="detail-label">Created</span>
                    <strong>{{ formatDate(dataset.created_at) }}</strong>
                  </div>
                </div>

                <div class="dataset-card__footer">
                  <span class="footer-note">Open to review the full dataset record</span>
                  <button type="button" class="view-dataset-btn">View Dataset</button>
                </div>
              </article>
            </div>

            <div v-else class="empty-state">
              <img src="../../../../assets/no-data.svg" alt="No datasets available" />
              <h3>No datasets available</h3>
              <p>There are no datasets matching the current search and filter combination.</p>
            </div>

            <div v-if="computedData.length" class="pagination-shell">
              <div class="pagination-copy">
                <p>Rows per page</p>
                <select id="dataset-page-size" v-model="perPage">
                  <option value="3">3</option>
                  <option value="5">5</option>
                  <option value="10">10</option>
                </select>
              </div>

              <b-pagination
                v-model="currentPage"
                :total-rows="computedData.length"
                :per-page="perPage"
                pills
                align="right"
                aria-controls="dataset-list"
              />
            </div>
          </template>
        </b-col>

        <!-- <b-col cols="12" xl="3" class="content-sidebar">
          <div class="sidebar-panel">
            <div class="sidebar-copy">
              <p class="sidebar-kicker">Refine inventory</p>
              <h3>Filter datasets</h3>
              <span>Apply structured filters to narrow the records currently on display.</span>
            </div>
            <FilterSideBar @filterObjs="fetchDataBasedOnSelectedRequirements" />
          </div>
        </b-col> -->
      </b-row>
    </section>
  </div>
</template>

<script>
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';
// import FilterSideBar from '../../components/Shared/FilterSideBar.vue';

export default {
  // components: {
  //   FilterSideBar,
  // },

  data() {
    return {
      search: '',
      isAll: false,
      isApproved: false,
      isPending: true,
      currentPage: 1,
      loading: true,
      perPage: 5,
      datasets: [],
      tempDatasets: [],
      programAreaMap: {},
    };
  },

  computed: {
    ...mapGetters('DATASET_STORE', ['getProject', 'getFilterData']),
    ...mapGetters('auth', ['user']),

    currentStatus() {
      if (this.isApproved) return 'Approved';
      if (this.isPending) return 'Pending';
      return 'All';
    },

    approvedCount() {
      return this.datasets.filter((dataset) => this.getDatasetStatus(dataset) === 'Approved')
        .length;
    },

    pendingCount() {
      return this.datasets.filter((dataset) => this.getDatasetStatus(dataset) === 'Pending').length;
    },

    currentViewTitle() {
      if (this.isApproved) return 'Approved datasets';
      if (this.isPending) return 'Pending datasets';
      return 'All datasets';
    },

    computedData() {
      return this.datasets.filter((dataset) => {
        const status = this.getDatasetStatus(dataset);
        const matchesStatus = this.currentStatus === 'All' || status === this.currentStatus;
        const datasetName = (dataset.name || '').toLowerCase();
        const matchesSearch = datasetName.includes(this.search.toLowerCase());

        return matchesStatus && matchesSearch;
      });
    },

    ComputedDatasets() {
      return this.computedData.slice(
        (this.currentPage - 1) * this.perPage,
        this.currentPage * this.perPage
      );
    },

    totalPages() {
      return Math.max(1, Math.ceil(this.computedData.length / this.perPage));
    },
  },

  watch: {
    perPage() {
      this.currentPage = 1;
    },

    search() {
      this.currentPage = 1;
    },

    getFilterData(val) {
      this.loading = true;

      if (!val || !Array.isArray(val.status) || !val.status.length) {
        this.datasets = [...this.tempDatasets];
        this.loading = false;
        return;
      }

      this.datasets = this.tempDatasets.filter((dataset) => {
        const completedState = dataset?.completed;
        return val.status.some((status) => {
          if (status === 'Completed') return completedState === true;
          if (status === 'Ongoing') return completedState === false;
          return this.getDatasetStatus(dataset) === status;
        });
      });

      this.loading = false;
    },
  },

  methods: {
    ...mapActions('DATASET_STORE', ['SETDATASET']),

    changeAll() {
      this.isAll = true;
      this.isApproved = false;
      this.isPending = false;
      this.currentPage = 1;
    },

    changeApprov() {
      this.isAll = false;
      this.isApproved = true;
      this.isPending = false;
      this.currentPage = 1;
    },

    changePend() {
      this.isAll = false;
      this.isApproved = false;
      this.isPending = true;
      this.currentPage = 1;
    },

    async fetchProgramAreas() {
      try {
        const { data } = await this.$dataset.getAllProgramAreas();
        const results = data?.results || [];

        this.programAreaMap = results.reduce((acc, item) => {
          acc[item.id] = item.name;
          return acc;
        }, {});
      } catch (error) {
        this.programAreaMap = {};
      }
    },

    async fetchDatasets() {
      this.loading = true;

      try {
        const datasets = await this.$dataset.getDatasets();
        this.datasets = datasets?.data?.results || [];
        this.tempDatasets = [...this.datasets];
      } finally {
        this.loading = false;
      }
    },

    async fetchDataBasedOnSelectedRequirements(data) {
      this.currentPage = 1;
      this.loading = true;

      try {
        const filtered = await this.$dataset.filteredData(data);
        this.datasets = filtered.data || [];
        this.tempDatasets = [...this.datasets];
      } finally {
        this.loading = false;
      }
    },

    async openDataset(id) {
      const singleDataset = this.datasets.find((dataset) => dataset.id === id);

      if (!singleDataset) return;

      this.SETDATASET(singleDataset);
      this.$router.push('/admin/data/sub-data');
    },

    getDatasetStatus(dataset) {
      return dataset?.status?.value || dataset?.status || 'Pending';
    },

    getTimeline(dataset) {
      if (!dataset?.start_date && !dataset?.end_date) return 'Timeline not provided';
      return `${this.getYear(dataset.start_date)} - ${this.getYear(dataset.end_date)}`;
    },

    getProgramAreas(dataset) {
      if (!Array.isArray(dataset?.program_area) || !dataset.program_area.length) {
        return 'Not specified';
      }

      return dataset.program_area
        .map((program) => this.programAreaMap[program] || program)
        .join(', ');
    },

    getDatasetSummary(dataset) {
      return (
        dataset?.description ||
        dataset?.summary ||
        'A curated dataset record prepared for administrative review, verification, and controlled access.'
      );
    },

    getYear(date) {
      if (!date) return 'N/A';
      return moment(date).format('YYYY');
    },

    formatDate(date) {
      if (!date) return 'Date unavailable';
      return moment(date).format('DD MMM YYYY');
    },

    statusBadgeClass(status) {
      return status === 'Approved' ? 'status-badge--approved' : 'status-badge--pending';
    },

    padIndex(index) {
      return String(index + 1).padStart(2, '0');
    },
  },

  async mounted() {
    await Promise.all([this.fetchProgramAreas(), this.fetchDatasets()]);
  },
};
</script>

<style lang="scss" scoped>
.dataset-page {
  min-height: 100vh;
  padding: 1.5rem 1rem 2rem;
  background: radial-gradient(circle at top left, rgba(222, 232, 244, 0.92), transparent 28%),
    radial-gradient(circle at top right, rgba(10, 61, 98, 0.12), transparent 24%),
    linear-gradient(180deg, #f4f0e8 0%, #f5f8fb 44%, #edf3f7 100%);
}

.dataset-shell {
  max-width: 1380px;
  margin: 0 auto;
}

.dataset-hero {
  display: grid;
  grid-template-columns: minmax(0, 1.8fr) minmax(280px, 1fr);
  gap: 1.5rem;
  padding: 1.75rem;
  border: 1px solid rgba(16, 56, 82, 0.08);
  border-radius: 28px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(245, 249, 252, 0.9)), #ffffff;
  box-shadow: 0 24px 60px rgba(18, 42, 60, 0.1);
}

.dataset-breadcrumb {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
  color: #6b7580;
  font-size: 0.9rem;
}

.breadcrumb-separator {
  opacity: 0.55;
}

.breadcrumb-current {
  color: #173952;
  font-weight: 600;
}

.hero-kicker,
.results-kicker,
.sidebar-kicker {
  margin-bottom: 0.65rem;
  color: #1a5d88;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.hero-copy h1 {
  max-width: 14ch;
  margin-bottom: 0.85rem;
  color: #102d42;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: clamp(2rem, 4vw, 3.35rem);
  font-weight: 700;
  line-height: 1.04;
}

.hero-description {
  max-width: 60ch;
  margin-bottom: 0;
  color: #51616d;
  font-size: 1rem;
  line-height: 1.75;
}

.hero-metrics {
  display: grid;
  gap: 1rem;
}

.metric-card {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding: 1.1rem 1.2rem;
  border: 1px solid rgba(16, 56, 82, 0.08);
  border-radius: 22px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.95), rgba(245, 248, 252, 0.9));
}

.metric-label {
  color: #607080;
  font-size: 0.88rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.metric-value {
  color: #102d42;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 2rem;
  line-height: 1.1;
}

.metric-meta {
  color: #7d8a95;
  font-size: 0.92rem;
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1.5rem;
  padding: 1rem 1.1rem;
  border: 1px solid rgba(16, 56, 82, 0.08);
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(18px);
}

.status-switcher {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.78rem 1rem;
  border: 1px solid rgba(25, 88, 132, 0.12);
  border-radius: 999px;
  background: rgba(247, 249, 252, 0.96);
  color: #35566f;
  font-weight: 600;
  transition: all 0.2s ease;
}

.status-pill:hover,
.status-pill--active {
  background: linear-gradient(135deg, #14476a, #2e6f9e);
  color: #fff;
  box-shadow: 0 16px 32px rgba(20, 71, 106, 0.2);
}

.pill-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2rem;
  height: 2rem;
  padding: 0 0.55rem;
  border-radius: 999px;
  background: rgba(17, 40, 59, 0.08);
  font-size: 0.86rem;
}

.status-pill--active .pill-count {
  background: rgba(255, 255, 255, 0.18);
}

.search-shell {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: min(100%, 340px);
  margin-bottom: 0;
  padding: 0.85rem 1rem;
  border: 1px solid rgba(16, 56, 82, 0.08);
  border-radius: 18px;
  background: #fafbfd;
  color: #70808d;
}

.search-shell input {
  width: 100%;
  border: 0;
  outline: none;
  background: transparent;
  color: #173952;
}

.content-grid {
  margin-top: 1.5rem;
}

.content-main {
  padding-right: 1.25rem;
}

.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  min-height: 420px;
  padding: 2rem;
  border: 1px solid rgba(16, 56, 82, 0.08);
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.9);
  text-align: center;
}

.loading-state p,
.empty-state p {
  margin-bottom: 0;
  color: #6d7882;
}

.empty-state img {
  width: min(100%, 360px);
}

.results-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.results-header h2,
.sidebar-copy h3 {
  margin-bottom: 0;
  color: #102d42;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: clamp(1.45rem, 2.6vw, 2rem);
  font-weight: 700;
}

.results-meta {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  color: #6a7884;
  font-weight: 500;
}

.results-divider {
  width: 1px;
  height: 1rem;
  background: rgba(95, 113, 128, 0.25);
}

.dataset-list {
  display: grid;
  gap: 1rem;
}

.premium-dataset-card {
  position: relative;
  overflow: hidden;
  padding: 1.45rem;
  border: 1px solid rgba(16, 56, 82, 0.08);
  border-radius: 26px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.96), rgba(247, 250, 252, 0.92)), #fff;
  box-shadow: 0 20px 40px rgba(20, 40, 56, 0.08);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.premium-dataset-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 26px 46px rgba(20, 40, 56, 0.12);
}

.card-glow {
  position: absolute;
  top: -80px;
  right: -20px;
  width: 220px;
  height: 220px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(57, 124, 172, 0.12), transparent 68%);
  pointer-events: none;
}

.dataset-card__top,
.dataset-card__footer,
.dataset-detail-grid {
  position: relative;
  z-index: 1;
}

.dataset-card__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.dataset-index {
  display: inline-block;
  margin-bottom: 0.5rem;
  color: #7a8f9f;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.dataset-card__top h3 {
  margin-bottom: 0;
  color: #173952;
  font-size: 1.35rem;
  font-weight: 700;
  line-height: 1.35;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.5rem 0.8rem;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.status-badge--approved {
  background: rgba(39, 120, 74, 0.12);
  color: #21643f;
}

.status-badge--pending {
  background: rgba(185, 129, 29, 0.14);
  color: #8f6417;
}

.dataset-summary {
  position: relative;
  z-index: 1;
  margin: 1rem 0 1.2rem;
  color: #586874;
  line-height: 1.7;
}

.dataset-detail-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.9rem;
}

.detail-card {
  padding: 0.95rem 1rem;
  border: 1px solid rgba(16, 56, 82, 0.08);
  border-radius: 18px;
  background: rgba(248, 250, 252, 0.92);
}

.detail-label {
  display: block;
  margin-bottom: 0.35rem;
  color: #728392;
  font-size: 0.82rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.detail-card strong {
  color: #173952;
  font-size: 0.98rem;
  line-height: 1.5;
}

.dataset-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1.2rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(16, 56, 82, 0.08);
}

.footer-note {
  color: #7a8791;
  font-size: 0.92rem;
}

.view-dataset-btn {
  border: 0;
  border-radius: 999px;
  padding: 0.8rem 1.15rem;
  background: linear-gradient(135deg, #154a70, #2f6f9d);
  color: #fff;
  font-weight: 700;
  box-shadow: 0 14px 24px rgba(21, 74, 112, 0.22);
}

.pagination-shell {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1.25rem;
  padding: 1rem 1.1rem;
  border: 1px solid rgba(16, 56, 82, 0.08);
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.86);
}

.pagination-copy {
  display: flex;
  align-items: center;
  gap: 0.8rem;
}

.pagination-copy p {
  margin-bottom: 0;
  color: #6f7d89;
}

.pagination-copy select {
  min-width: 72px;
  padding: 0.55rem 0.75rem;
  border: 1px solid rgba(16, 56, 82, 0.12);
  border-radius: 14px;
  background: #fff;
}

.content-sidebar {
  padding-left: 1.25rem;
}

.sidebar-panel {
  position: sticky;
  top: 1.5rem;
  padding: 1.2rem;
  border: 1px solid rgba(16, 56, 82, 0.08);
  border-radius: 26px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.94), rgba(244, 248, 251, 0.88)), #fff;
  box-shadow: 0 20px 38px rgba(20, 40, 56, 0.08);
}

.sidebar-copy {
  margin-bottom: 1rem;
}

.sidebar-copy span {
  color: #6b7985;
  line-height: 1.6;
}

::v-deep .pagination .page-item .page-link {
  border: 0;
  border-radius: 999px;
  margin: 0 0.2rem;
  color: #18476a;
  background: #edf4f8;
}

::v-deep .pagination .page-item.active .page-link {
  background: linear-gradient(135deg, #14476a, #2e6f9e);
  color: #fff;
  box-shadow: 0 10px 20px rgba(20, 71, 106, 0.18);
}

@media (max-width: 1199px) {
  .content-main,
  .content-sidebar {
    padding-right: 0;
    padding-left: 0;
  }

  .content-sidebar {
    margin-top: 1.5rem;
  }

  .sidebar-panel {
    position: static;
  }
}

@media (max-width: 991px) {
  .dataset-page {
    padding-top: 0;
  }

  .dataset-hero,
  .dataset-detail-grid {
    grid-template-columns: 1fr;
  }

  .toolbar,
  .results-header,
  .pagination-shell,
  .dataset-card__footer {
    flex-direction: column;
    align-items: stretch;
  }

  .results-meta {
    justify-content: space-between;
  }

  .search-shell {
    width: 100%;
    min-width: 0;
  }
}

@media (max-width: 767px) {
  .dataset-page {
    padding: 0;
  }

  .dataset-hero,
  .toolbar,
  .premium-dataset-card,
  .sidebar-panel,
  .pagination-shell,
  .loading-state,
  .empty-state {
    border-radius: 22px;
  }

  .dataset-card__top {
    flex-direction: column;
  }

  .view-dataset-btn {
    width: 100%;
  }
}

@media (max-width: 600px) {
  .dataset-page {
    padding-top: 0;
  }
}
</style>
